import axios from 'axios';
import { API_BASE_URL } from './config'

// Axios response interceptor to handle 401 errors globally
axios.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401) {
            ['adminToken', 'adminId', 'adminUsername', 'adminRoles'].forEach(k => localStorage.removeItem(k));
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

// Function to handle login (unified for admin and user)
export const login = async (username, password) => {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    return await axios.post(`${API_BASE_URL}/token`, params, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
};

// Function to update password
export const updatePassword = async (token, currentPassword, newPassword) => {
    return await axios.put(`${API_BASE_URL}/update-password`, {
        "username": "admin",
        "currentPassword": currentPassword,
        "newPassword": newPassword,
    }, {
        headers: { Authorization: `Bearer ${token}` },
    });
};

// apikey management

// Function to generate API key
export const generateApiKey = async (token, username, email, expiry, userRoles) => {
    return await axios.post(`${API_BASE_URL}/generate-key`, {
        "username": username,
        "email": email,
        "expiry": expiry,
        "user_role": userRoles,
    }, {
        headers: { Authorization: `Bearer ${token}` },
    });
};

// Function to get list of API keys
export const getApiKeys = async (token) => {
    return await axios.get(`${API_BASE_URL}/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
    });
};

// Function to delete an API key
export const deleteApiKey = async (token, apiKeyId) => {
    console.log(apiKeyId)
    return await axios.delete(`${API_BASE_URL}/api-keys/${apiKeyId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
};


// model route config

export const saveRoutingConfig = async (token, configData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/save-config`, 
        configData, 
        {
            headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

export const getRoutingConfigs = async (token) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/get-configs`, 
        {
            headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

export const deleteRoutingConfig = async (token, payload) => {
  try {
    // Sending DELETE request with routing_key as a query parameter
    const response = await axios.delete(`${API_BASE_URL}/delete-configs`, {
      params: payload,  // routing_key is passed as a query parameter
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

// role assignment

// Get all available client roles
export const getAllClientRoles = async (token) => {
  return await axios.get(`${API_BASE_URL}/client_roles`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

// Assign roles
export const assignRole = async (token, username, roleList) => {
  return await axios.post(
      `${API_BASE_URL}/assign_role`,
      { username: username, role_list: roleList },
      { headers: { Authorization: `Bearer ${token}` } }
  );
};

// Unassign roles
export const unassignRole = async (token, username, roleList) => {
  return await axios.post(
      `${API_BASE_URL}/unassign_role`,
      { username: username, role_list: roleList },
      { headers: { Authorization: `Bearer ${token}` } }
  );
};


// Manage deployed Model

export const getDeployedModel = async (token) => {
  return await axios.get(`${API_BASE_URL}/deployed_model`,
    {
        headers: { Authorization: `Bearer ${token}` },
    }
  ).then(response => {
      if(response.data.success){
        return response.data.data;
      }else{
        alert("Got Some issue Please try again")
        return []
      }
    })
    .catch(error => {
      console.error('Error fetching models:', error);
      return []
    });
}


// manage pipeline

export const getAllRunningPipelines = async (adminId, token) => {
  const user = {
    user_id: adminId,
    user_name: 'admin',
    user_email: '',
    api_key: '',
    user_role: ['admin'],
  };
  return axios.post(`${API_BASE_URL}/get_all_running_pipeline`, 
    user,
    {
      headers: { Authorization: `Bearer ${token}` },
  });
};

export const killJob = async (taskId, reason, token) => {
  return axios.delete(`${API_BASE_URL}/kill_job`, {
    headers: { Authorization: `Bearer ${token}` },
    params: {
      task_id: taskId,
      reason,
    },
  });
};


export const getAllPipelines = async (adminId, token) => {

  const user = {
    user_id: adminId,
    user_name: 'admin',
    user_email: '',
    api_key: '',
    user_role: ['admin'],
  };
  try{
    const response =  await axios.post(`${API_BASE_URL}/all_pipeline`, 
      user,
      {
        headers: { Authorization: `Bearer ${token}` },
    });

    return response

  }catch(error){
    console.error('Error deleting pipeline:', error);
    throw error;
  }
};

export const deletePipeline = async (pipelineId, token) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/delete_pipeline`, {
      params: { pipeline_id: pipelineId }, 
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};


export const getJobDetails = async (requestData, token) => {
  const response = await fetch(`${API_BASE_URL}/get_all_job_status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestData),
  });
  return response.json();
};

// Exported models management

export const getExportedModels = async (token) => {
  return await axios.get(`${API_BASE_URL}/export_model`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const getExportedModelDownloadUrl = async (token, modelName, version, fileType = 'artifact') => {
  return await axios.get(`${API_BASE_URL}/export_model/download-url`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { model_name: modelName, version, file_type: fileType }
  });
};

// Batch inference history management

export const getBatchInferenceHistory = async (token, params) => {
  return await axios.get(`${API_BASE_URL}/batch-inference/history`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });
};

export const getBatchInferencePresignedUrl = async (token, s3Path) => {
  return await axios.get(`${API_BASE_URL}/batch-inference/presigned-download`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { s3_path: s3Path }
  });
};

// Prediction audit trail management

export const getAuditPredictions = async (token, params) => {
  return await axios.get(`${API_BASE_URL}/audit/predictions`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });
};

export const getAuditPredictionDetail = async (token, uid) => {
  return await axios.get(`${API_BASE_URL}/audit/predictions/${uid}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const generateExplanation = async (token, uid) => {
  return await axios.get(`${API_BASE_URL}/explanation/${uid}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

// Drift report management

export const getDriftReportAvailableMonths = async (token) => {
  return await axios.get(`${API_BASE_URL}/drift_report/available-months`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const triggerDriftReport = async (token, params) => {
  return await axios.post(`${API_BASE_URL}/drift_report/run`, null, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
};

export const getDriftReportStatus = async (token, uid) => {
  return await axios.get(`${API_BASE_URL}/drift_report/status/${uid}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const browseDriftReport = async (token, uid) => {
  return await axios.get(`${API_BASE_URL}/drift_report/browse/${uid}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const getDriftReportHistory = async (token, params) => {
  return await axios.get(`${API_BASE_URL}/drift_report/history`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
};

export const getDriftReportPresignedUrl = async (token, s3Path) => {
  return await axios.get(`${API_BASE_URL}/drift_report/presigned-url`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { s3_path: s3Path },
  });
};

export const deleteDriftReport = async (token, uid) => {
  return await axios.delete(`${API_BASE_URL}/drift_report/${uid}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const downloadDriftReportZip = async (token, files, zipName) => {
  return await axios.post(
    `${API_BASE_URL}/drift_report/download-zip`,
    { files, zip_name: zipName },
    {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    }
  );
};
